import { useCallback, useEffect, useState } from 'react'
import { AppDialog, type AppDialogAction } from '../Common/AppDialog'
import { Button } from '../Common/Button'
import { TextInput } from '../Common'
import { useNls } from '../../nls/nlsProvider'
import {
  createAccountTransferCode,
  confirmAccountTransfer,
  mapAccountTransferError,
} from '../../services/accountTransferService'

export interface MoveToGoogleAccountModalProps {
  open: boolean
  onClose: () => void
}

export const MoveToGoogleAccountModal = ({
  open,
  onClose,
}: MoveToGoogleAccountModalProps) => {
  const { t } = useNls()
  const [step, setStep] = useState<'EXPLAIN' | 'GENERATED'>('EXPLAIN')
  const [code, setCode] = useState<string | null>(null)
  const [expiresAt, setExpiresAt] = useState<number | null>(null)
  const [isOwner, setIsOwner] = useState(false)
  const [confirmToken, setConfirmToken] = useState('')
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [secondsRemaining, setSecondsRemaining] = useState<number>(15 * 60)

  const handleDismiss = useCallback(() => {
    setStep('EXPLAIN')
    setCode(null)
    setExpiresAt(null)
    setIsOwner(false)
    setConfirmToken('')
    setIsConfirmed(false)
    setError(null)
    onClose()
  }, [onClose])

  // Countdown timer
  useEffect(() => {
    if (!expiresAt) return

    const updateTimer = () => {
      const diff = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000))
      setSecondsRemaining(diff)
    }

    updateTimer()
    const interval = setInterval(updateTimer, 1000)
    return () => clearInterval(interval)
  }, [expiresAt])

  const handleGenerateCode = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await createAccountTransferCode()
      setCode(res.code)
      setExpiresAt(res.expiresAtMs)
      setIsOwner(res.requiresEmailConfirmation)
      setStep('GENERATED')
    } catch (err) {
      const mapped = mapAccountTransferError(err)
      setError(t(mapped.nlsKey))
    } finally {
      setLoading(false)
    }
  }, [t])

  const handleConfirmToken = useCallback(async () => {
    if (!confirmToken.trim() || !code) return
    setConfirming(true)
    setError(null)
    try {
      await confirmAccountTransfer({ code, confirmToken: confirmToken.trim() })
      setIsConfirmed(true)
    } catch (err) {
      const mapped = mapAccountTransferError(err)
      setError(t(mapped.nlsKey))
    } finally {
      setConfirming(false)
    }
  }, [code, confirmToken, t])

  const formatCountdown = () => {
    if (secondsRemaining <= 0) {
      return t('NLSAccountTransferCodeExpired')
    }
    const mins = Math.floor(secondsRemaining / 60).toString().padStart(2, '0')
    const secs = (secondsRemaining % 60).toString().padStart(2, '0')
    return t('NLSAccountTransferExpiresIn', { minutes: mins, seconds: secs })
  }

  const dialogActions: AppDialogAction[] =
    step === 'EXPLAIN'
      ? [
          {
            label: 'Cancel',
            variant: 'ghost',
            onClick: onClose,
            disabled: loading,
          },
          {
            label: 'Generate code',
            variant: 'primary',
            onClick: handleGenerateCode,
            loading,
            disabled: loading,
          },
        ]
      : [
          {
            label: 'Done',
            variant: 'primary',
            onClick: onClose,
          },
        ]

  return (
    <AppDialog
      open={open}
      onDismiss={handleDismiss}
      title={t('NLSAccountTransferDialogTitle')}
      message={
        <div className="accountTransferModalBody flex flex-col gap-4 text-sm">
          {step === 'EXPLAIN' ? (
            <>
              <div className="p-3 rounded-lg bg-[var(--surface-muted)] flex flex-col gap-2">
                <p className="font-semibold text-[var(--color-primary)]">
                  {t('NLSAccountTransferExplainMoves')}
                </p>
                <p className="text-[var(--muted)]">
                  {t('NLSAccountTransferExplainStays')}
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="p-4 rounded-lg bg-[var(--surface-muted)] text-center flex flex-col gap-2">
                <span className="text-xs uppercase tracking-wider text-[var(--muted)]">
                  {t('NLSAccountTransferCodeHeading')}
                </span>
                <strong className="text-2xl font-mono tracking-widest text-[var(--color-primary)]">
                  {code}
                </strong>
                <span className="text-xs text-[var(--muted)]">
                  {formatCountdown()}
                </span>
              </div>

              {isOwner && (
                <div className="p-3 rounded-lg border border-[var(--border-subtle)] flex flex-col gap-3">
                  <p className="text-xs text-[var(--muted)]">
                    {t('NLSAccountTransferOwnerEmailCheck')}
                  </p>
                  {isConfirmed ? (
                    <p className="text-xs font-semibold text-[var(--success)]">
                      {t('NLSAccountTransferConfirmedNotice')}
                    </p>
                  ) : (
                    <div className="flex gap-2 items-center">
                      <TextInput
                        block
                        value={confirmToken}
                        onChange={setConfirmToken}
                        placeholder={t('NLSAccountTransferPasteTokenPlaceholder')}
                        disabled={confirming}
                      />
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => void handleConfirmToken()}
                        disabled={!confirmToken.trim() || confirming}
                        loading={confirming}
                      >
                        {t('NLSAccountTransferConfirmTokenButton')}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {error && <p className="text-xs text-[var(--danger)]">{error}</p>}
        </div>
      }
      actions={dialogActions}
    />
  )
}
